/**
 * Swiper Gallery Lightbox
 * Opens a bottom sheet lightbox for any `.swiper-gallery-block` image.
 */

import { Swiper } from 'swiper';
import { Navigation, Keyboard } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';

export default class SwiperGalleryLightbox {
    constructor() {
        this.lightboxRoot = null;
        this.swiper = null;
        this.activeImages = [];
        this.boundEscHandler = (e) => {
            if (e.key === 'Escape') {
                this.close();
            }
        };
        this.init();
    }

    init() {
        // Delegate click from gallery images
        document.addEventListener('click', (e) => {
            const img = e.target?.closest('.swiper-gallery-block .swiper-slide img');
            if (!img) return;

            // Build image list from the clicked gallery block
            const block = img.closest('.swiper-gallery-block');
            if (!block) return;

            const images = Array.from(block.querySelectorAll('.swiper-gallery-swiper .swiper-slide img'))
                .map((node) => ({
                    src: node.currentSrc || node.src,
                    alt: node.getAttribute('alt') || '',
                }));

            const startIndex = Math.max(0, images.findIndex(i => (i.src === (img.currentSrc || img.src))));

            // Find the server-rendered lightbox corresponding to this block
            let lightboxRoot = null;
            const next = block.nextElementSibling;
            if (next && next.matches('[data-sg-lightbox]')) {
                lightboxRoot = next;
            } else {
                lightboxRoot = document.querySelector('[data-sg-lightbox]');
            }

            this.open({ images, startIndex, lightboxRoot });
        });
    }

    ensureLightboxDOM(root) {
        if (!root) return;
        this.lightboxRoot = root;
        if (!root.dataset.boundClose) {
            const closeBtn = root.querySelector('[data-sg-close]');
            if (closeBtn) closeBtn.addEventListener('click', () => this.close());
            // Close when clicking overlay outside the panel
            root.addEventListener('click', (e) => {
                const panel = root.querySelector('[data-sg-panel]');
                if (!panel) return;
                if (!panel.contains(e.target)) {
                    this.close();
                }
            });
            root.dataset.boundClose = 'true';
        }
    }

    buildSlides(images) {
        const wrapper = this.lightboxRoot.querySelector('.swiper-gallery-lightbox-swiper .swiper-wrapper');
        wrapper.innerHTML = '';
        images.forEach(({ src, alt }) => {
            const slide = document.createElement('div');
            slide.className = 'swiper-slide';
            slide.innerHTML = `<div class="sg-slide-inner"><img src="${src}" alt="${alt}"></div>`;
            wrapper.appendChild(slide);
        });
    }

    initSwiper(startIndex) {
        // Hook navigation buttons
        const nextEl = this.lightboxRoot.querySelector('[data-sg-next]');
        const prevEl = this.lightboxRoot.querySelector('[data-sg-prev]');

        // Destroy old instance if any
        if (this.swiper) {
            try { this.swiper.destroy(true, true); } catch (e) { }
            this.swiper = null;
        }

        this.swiper = new Swiper(this.lightboxRoot.querySelector('.swiper-gallery-lightbox-swiper'), {
            modules: [Navigation, Keyboard],
            slidesPerView: 'auto',
            spaceBetween: 16,
            grabCursor: true,
            simulateTouch: true,
            preventClicks: false,
            preventClicksPropagation: false,
            slideToClickedSlide: false,
            navigation: { nextEl, prevEl },
            keyboard: { enabled: true },
            initialSlide: Number.isFinite(startIndex) ? startIndex : 0
        });
    }

    // Rely on CSS for consistent slide heights; do not set inline sizes

    open({ images, startIndex = 0, lightboxRoot = null }) {
        if (!images || !images.length) return;
        this.ensureLightboxDOM(lightboxRoot);

        // Use server-rendered slides and init Swiper
        this.activeImages = images;
        this.initSwiper(startIndex);

        // Show with slide-in animation
        this.lightboxRoot.setAttribute('aria-hidden', 'false');
        const panel = this.lightboxRoot.querySelector('[data-sg-panel]');
        if (panel) panel.classList.remove('translate-y-full');
        document.body.classList.add('nav-overlay-open');
        document.body.classList.add('sg-lightbox-open');

        // Bind escape key
        document.addEventListener('keydown', this.boundEscHandler);

        // Re-size images once visible
        requestAnimationFrame(() => this.sizeImagesForContainer());
    }

    close() {
        if (!this.lightboxRoot) return;
        const panel = this.lightboxRoot.querySelector('[data-sg-panel]');
        if (panel) panel.classList.add('translate-y-full');
        this.lightboxRoot.setAttribute('aria-hidden', 'true');
        document.body.classList.remove('nav-overlay-open');
        document.body.classList.remove('sg-lightbox-open');

        // Destroy swiper instance
        if (this.swiper) {
            try { this.swiper.destroy(true, true); } catch (e) { }
            this.swiper = null;
        }

        // Unbind escape
        document.removeEventListener('keydown', this.boundEscHandler);
    }
}


